import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Mail, ShieldCheck } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/apiClient';
import Impersonation from './Impersonation';

// Settings → Users & Roles. Portfolio owner / manager add & manage their
// team: facility_manager / staff / viewer only (escalation blocked
// server-side). Passwordless — invitee gets an email link, then signs in
// with a code, same as provisioned owners.
// Two tabs since 2026-08-06 (owner): Team, and Tenant Impersonation —
// one people-and-access surface.

const ROLES = [
  { value: 'facility_manager', label: 'Facility Manager',
    desc: 'Manages assigned facilities (no merchant/payout config)' },
  { value: 'staff', label: 'Staff',
    desc: 'Read + record payments on assigned facilities' },
  { value: 'viewer', label: 'Viewer', desc: 'Read-only, no changes' },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
const ROLE_STYLE = {
  facility_manager: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  staff: 'bg-cyan-50 text-cyan-700 ring-cyan-600/20',
  viewer: 'bg-slate-100 text-slate-600 ring-slate-500/20',
};
const blank = { email: '', full_name: '', role: 'staff', facility_ids: [] };


// Your own sign-in method. Email-code works for every operator and is the
// default; a password is opt-in. Password login already existed at
// /auth/login but nothing could CREATE an operator password except an admin
// editing someone else, so the option was unreachable by the person who
// wanted it.
const MyPasswordPanel = () => {
  const { user } = useAuth();
  // Only facility_manager and above set their OWN session length. Staff have
  // theirs set at creation and changed by a manager — a shared counter login
  // extending itself to 30 days is what a session limit exists to prevent.
  // The server enforces this too (403); hiding it just avoids offering a
  // control that would be refused.
  const RANK = ['portfolio_owner', 'portfolio_manager',
                'facility_owner', 'facility_manager'];
  const canSetOwnSession = (
    (user?.roles?.length ? user.roles : [user?.role]) || []
  ).some((r) => RANK.includes(String(r || '').toLowerCase()));
  const [open, setOpen] = useState(false);
  // Session length lives beside the password because both answer "how do I
  // get in, and how long do I stay in". Default (blank) is the platform's;
  // a phone used all day wants the long end, a shared counter terminal the
  // short end — and only the person holding the device knows which.
  const [timeout_, setTimeout_] = useState('');
  const [platformDefaultMins, setPlatformDefaultMins] = useState(null);
  const [tzBusy, setTzBusy] = useState(false);
  // The default is NAMED, not left abstract. "Platform default" alone gave
  // an operator no way to know whether picking it meant an hour or a month —
  // and this control is a security setting, so an unknowable value is the one
  // thing it must not be. Sourced from the API so it cannot drift from config.
  const defaultLabel = platformDefaultMins
    ? `Platform default (${platformDefaultMins >= 1440
        ? `${Math.round(platformDefaultMins / 1440)} day${platformDefaultMins >= 2880 ? 's' : ''}`
        : `${Math.round(platformDefaultMins / 60)} hours`})`
    : 'Platform default';
  const SESSION_CHOICES = [
    ['', defaultLabel],
    ['60', '1 hour'],
    ['480', '8 hours — a work day'],
    ['1440', '1 day'],
    ['10080', '1 week'],
    ['43200', '30 days — stay signed in on my phone'],
  ];

  useEffect(() => {
    if (!canSetOwnSession) return;
    apiClient.get('/api/auth/me')
      .then(({ data }) => {
        // /api/auth/me did not return session_timeout_minutes at all, so this
        // was always undefined and the control re-rendered "Platform default"
        // after every save — the setting saved correctly and looked broken.
        const v = data?.session_timeout_minutes;
        setTimeout_(v ? String(v) : '');
        setPlatformDefaultMins(data?.platform_session_default_minutes || null);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSetOwnSession]);

  const saveTimeout = async (value) => {
    setTimeout_(value);
    setTzBusy(true);
    try {
      await apiClient.patch('/api/auth/me/preferences', {
        session_timeout_minutes: value ? parseInt(value, 10) : null,
      });
      toast.success(value
        ? 'Saved — applies next time you sign in.'
        : 'Using the platform default.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally {
      setTzBusy(false);
    }
  };
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [hasPassword, setHasPassword] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setErr(''); setMsg('');
    if (next.length < 8) { setErr('Use at least 8 characters.'); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post('/api/auth/me/password', {
        new_password: next,
        current_password: current || null,
      });
      setHasPassword(true);
      setMsg(data.had_password
        ? 'Password updated.'
        : 'Password set. You can now sign in either way.');
      setCurrent(''); setNext('');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not save the password');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(
      'Remove your password and use email codes only? '
      + 'You can always sign in with an emailed code, so this cannot lock you out.'
    )) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await apiClient.delete('/api/auth/me/password');
      setHasPassword(false);
      setMsg('Password removed. Sign in with an emailed code.');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not remove the password');
    } finally { setBusy(false); }
  };

  return (
    <div className="mb-6 rounded-xl border border-slate-200/80 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] font-semibold text-slate-800">Your sign-in</p>
          <p className="text-[12px] text-slate-500 mt-0.5">
            You always can sign in with a code emailed to you. Optionally set a
            password to sign in without waiting for the email.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="shrink-0 text-[12px] font-medium text-indigo-600 hover:text-indigo-700"
        >
          {open ? 'Close' : 'Set a password'}
        </button>
      </div>

      {canSetOwnSession && (
      <div className="mt-3 pt-3 border-t border-slate-100">
        <label className="block text-[12px] font-medium text-slate-600 mb-1">
          Stay signed in for
        </label>
        <select
          value={timeout_}
          disabled={tzBusy}
          onChange={(e) => saveTimeout(e.target.value)}
          className="w-full sm:w-72 border border-slate-300 rounded-md px-3 py-1.5 text-[13px] bg-white disabled:opacity-50"
        >
          {SESSION_CHOICES.map(([v, label]) => (
            <option key={v} value={v}>{label}</option>
          ))}
        </select>
        <p className="text-[11px] text-slate-400 mt-1">
          Applies the next time you sign in, not to this session. Pick a long
          window on your own phone; keep it short on a shared counter machine.
        </p>
      </div>
      )}

      {open && (
        <form onSubmit={save} className="mt-3 space-y-2">
          {msg && <p className="text-[12px] text-emerald-700">{msg}</p>}
          {err && <p className="text-[12px] text-rose-600">{err}</p>}
          <div className="flex flex-wrap gap-2">
            {/* Only needed when one already exists — the server enforces this
                too, so a borrowed session cannot silently rotate it. */}
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="Current password (if you have one)"
              className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
            />
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="New password (8+ characters)"
              className="flex-1 min-w-[200px] rounded-lg border border-slate-200 px-3 py-1.5 text-[13px]"
            />
            <button
              type="submit"
              disabled={busy}
              className="px-3 rounded-lg bg-indigo-600 text-white text-[13px] disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
          {hasPassword !== false && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="text-[12px] text-slate-500 hover:text-rose-600"
            >
              Remove my password and use email codes only
            </button>
          )}
        </form>
      )}
    </div>
  );
};


const USERS_TABS = [
  { key: 'team', label: 'Team' },
  { key: 'impersonation', label: 'Tenant Impersonation' },
];

const TeamUsers = () => {
  const { facilities } = useFacility();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const usersTab = USERS_TABS.find((t) => t.key === rawTab) ? rawTab : 'team';
  const setUsersTab = (key) => {
    const params = new URLSearchParams(searchParams);
    if (key === 'team') params.delete('tab');
    else params.set('tab', key);
    setSearchParams(params, { replace: true });
  };
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // 'new' | id
  const [form, setForm] = useState(blank);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get('/api/auth/team');
      setTeam(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load team');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Roles this surface may assign. An owner/manager opened in edit is
  // NOT role-editable here (changing an owner is out of scope) — we keep
  // their original role and never send it back.
  const ASSIGNABLE = ['facility_manager', 'staff', 'viewer'];
  const [origRole, setOrigRole] = useState(null);

  const startNew = () => {
    setForm(blank);
    setOrigRole(null);
    setEditing('new');
    setError('');
  };
  const startEdit = (m) => {
    setForm({
      email: m.email,
      full_name: m.full_name,
      // If the member isn't an assignable role (e.g. portfolio_owner),
      // default the picker to a safe value but we won't send role.
      role: ASSIGNABLE.includes(m.role) ? m.role : 'staff',
      facility_ids: m.facility_ids || [],
    });
    setOrigRole(m.role);
    setEditing(m._id);
    setError('');
  };

  const toggleFacility = (fid) =>
    setForm((f) => ({
      ...f,
      facility_ids: f.facility_ids.includes(fid)
        ? f.facility_ids.filter((x) => x !== fid)
        : [...f.facility_ids, fid],
    }));

  const save = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (editing === 'new') {
        await apiClient.post('/api/auth/team', {
          email: form.email.trim().toLowerCase(),
          full_name: form.full_name.trim(),
          role: form.role,
          facility_ids: form.facility_ids,
        });
      } else {
        const payload = {
          full_name: form.full_name.trim(),
          facility_ids: form.facility_ids,
        };
        // Only send role when this member is actually role-editable
        // here (an assignable role). For an owner, we never send it —
        // name/facility edits still save cleanly.
        if (ASSIGNABLE.includes(origRole)) {
          payload.role = form.role;
        }
        await apiClient.put(`/api/auth/team/${editing}`, payload);
      }
      setEditing(null);
      await load();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.full_name} (${m.email})?`)) return;
    try {
      await apiClient.delete(`/api/auth/team/${m._id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Remove failed');
    }
  };

  const toggleActive = async (m) => {
    try {
      await apiClient.put(`/api/auth/team/${m._id}`, {
        is_active: !m.is_active,
      });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Update failed');
    }
  };

  const facById = Object.fromEntries(
    (facilities || []).map((f) => [f._id, f.name]),
  );
  const input =
    'w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all';

  return (
    <div className="max-w-4xl">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600">
            Settings
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Users &amp; Roles
          </h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            {usersTab === 'impersonation'
              ? 'View a tenant’s portal from their perspective. All actions audit-logged; 15-minute sessions.'
              : 'Invite teammates. They sign in with an emailed code, and can optionally set a password of their own. You can add facility managers, staff, and viewers.'}
          </p>
        </div>
        {usersTab === 'team' && (
          <button
            onClick={startNew}
            className="inline-flex items-center gap-1.5 py-2 px-3.5 rounded-lg bg-indigo-600 text-white text-[13px] font-semibold shadow-sm hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" /> Invite member
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 mb-5">
        {USERS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setUsersTab(t.key)}
            className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-medium transition-colors ${
              usersTab === t.key
                ? 'bg-indigo-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {usersTab === 'impersonation' && <Impersonation embedded />}

      {usersTab === 'team' && (
      <>
      <MyPasswordPanel />

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200/80 px-3 py-2.5 text-[13px] text-rose-700">
          {error}
        </div>
      )}

      {editing && (
        <form
          onSubmit={save}
          className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                Full name
              </label>
              <input
                className={input}
                value={form.full_name}
                onChange={(e) =>
                  setForm({ ...form, full_name: e.target.value })}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                Email {editing !== 'new' && '(cannot change)'}
              </label>
              <input
                className={input}
                type="email"
                disabled={editing !== 'new'}
                value={form.email}
                onChange={(e) =>
                  setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
              Role
            </label>
            {editing !== 'new' && origRole && !ASSIGNABLE.includes(origRole) ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-[12px] text-slate-600">
                This account is a{' '}
                <span className="font-semibold capitalize">
                  {String(origRole).replace(/_/g, ' ')}
                </span>{' '}
                — its role can't be changed here. You can still update the
                name and facility access.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {ROLES.map((r) => (
                  <button
                    type="button"
                    key={r.value}
                    onClick={() => setForm({ ...form, role: r.value })}
                    className={`text-left rounded-lg border p-3 transition-all ${
                      form.role === r.value
                        ? 'border-indigo-500 ring-2 ring-indigo-500/20 bg-indigo-50/50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="text-[13px] font-semibold text-slate-900">
                      {r.label}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {r.desc}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {(facilities || []).length > 0 && (
            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                Facility access{' '}
                <span className="text-slate-400 font-normal">
                  (which facilities this member can work in)
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                {facilities.map((f) => {
                  const on = form.facility_ids.includes(f._id);
                  return (
                    <button
                      type="button"
                      key={f._id}
                      onClick={() => toggleFacility(f._id)}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-medium ring-1 transition-all ${
                        on
                          ? 'bg-indigo-600 text-white ring-indigo-600'
                          : 'bg-white text-slate-600 ring-slate-200 hover:ring-slate-300'
                      }`}
                    >
                      {f.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={busy}
              className="py-2 px-4 rounded-lg bg-indigo-600 text-white text-[13px] font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {busy
                ? 'Saving…'
                : editing === 'new'
                ? 'Send invite'
                : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="py-2 px-4 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-50/60 text-left">
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Member
                </th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Role
                </th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Facilities
                </th>
                <th className="px-5 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Status
                </th>
                <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              ) : team.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-slate-400">
                    No team members yet. Invite your first one.
                  </td>
                </tr>
              ) : (
                team.map((m) => (
                  <tr key={m._id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <p className="font-medium text-slate-900">
                        {m.full_name}
                      </p>
                      <p className="text-[12px] text-slate-500 flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {m.email}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
                          ROLE_STYLE[m.role] ||
                          'bg-slate-100 text-slate-600 ring-slate-500/20'
                        }`}
                      >
                        <ShieldCheck className="h-3 w-3" />
                        {ROLE_LABEL[m.role] || m.role}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-500">
                      {(m.facility_ids || []).length === 0
                        ? '—'
                        : m.facility_ids
                            .map((id) => facById[id] || id)
                            .join(', ')}
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={() => toggleActive(m)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
                          m.is_active
                            ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
                            : 'bg-slate-100 text-slate-500 ring-slate-500/20'
                        }`}
                        title="Toggle active"
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            m.is_active ? 'bg-emerald-500' : 'bg-slate-400'
                          }`}
                        />
                        {m.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => startEdit(m)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {/* No delete for a portfolio_owner row or for
                            yourself — owners are provisioned only by
                            platform support and cannot self-remove (just
                            as they cannot self-provision). */}
                        {m.role !== 'portfolio_owner' &&
                          m._id !== user?.id && (
                            <button
                              onClick={() => remove(m)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                              title="Remove"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default TeamUsers;
